import { useCallback, useEffect, useState } from "react";
import { askExtractChat } from "../actions/extract-chat/askExtractChat";

export interface ExtractChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface UseExtractChatOptions {
  jobId: string;
  /**
   * Optional inline extracted text. When provided, it's passed straight to
   * the backend so the chat doesn't depend on the job's metadata.contentTextKey
   * having been written. Recommended whenever the caller already has the text
   * in memory (post-upload streaming, QA-Assist response injection, etc.).
   */
  extractedText?: string;
}

interface UseExtractChatReturn {
  messages: ExtractChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (question: string) => Promise<void>;
  clearMessages: () => void;
}

const createMessage = (
  role: ExtractChatMessage["role"],
  content: string,
): ExtractChatMessage => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  content,
  timestamp: new Date(),
});

export const useExtractChat = ({ jobId, extractedText }: UseExtractChatOptions): UseExtractChatReturn => {
  const [messages, setMessages] = useState<ExtractChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMessages([]);
    setIsLoading(false);
    setError(null);
  }, [jobId]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const sendMessage = useCallback(async (question: string) => {
    const trimmedQuestion = question.trim();
    if (!jobId || !trimmedQuestion || isLoading) {
      return;
    }

    const nextUserMessage = createMessage("user", trimmedQuestion);
    const historyForRequest = messages.map((message) => ({
      role: message.role,
      content: message.content,
    }));

    setMessages((current) => [...current, nextUserMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const answer = await askExtractChat(
        jobId,
        trimmedQuestion,
        historyForRequest,
        extractedText ? { extractedText } : {},
      );
      setMessages((current) => [...current, createMessage("assistant", answer || "Not found in the uploaded content.")]);
    } catch (requestError: any) {
      setError(requestError.message || "Something went wrong while asking about the extracted content.");
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, jobId, messages, extractedText]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
  };
};

export default useExtractChat;
